import { FormModal, SubmitProps } from '@/components/form-modal.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { bulkUpdateStudentProfiles } from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import { StudentOfSchool } from '@airreading/common/data';
import { useMutation } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { z } from 'zod';

interface BulkResetStartingLessonsProps {
  students: StudentOfSchool[];
  trigger: ReactNode;
  onSuccess?: () => void;
}

const formSchema = z.object({});

export const BulkResetStartingLessons = ({ students, trigger, onSuccess }: BulkResetStartingLessonsProps) => {
  const { toast } = useToast();

  const { mutateAsync } = useMutation({
    mutationFn: bulkUpdateStudentProfiles,
  });

  const onSubmit = ({ closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    return mutateAsync({
      studentIds: students.map(({ id }) => id),
      options: {
        level: null,
      },
    })
      .then(() => {
        toast({
          title: `Reset starting lesson for ${students.length} student(s)`,
        });
        onSuccess?.();
        closeModal();
      })
      .catch((e) => {
        toast({
          title: 'Error resetting starting lesson',
          description: airServerErrorFrom(e)?.message,
          variant: 'destructive',
        });
      });
  };

  return (
    <FormModal
      trigger={trigger}
      modalTitle={`Reset starting lesson`}
      formSchema={formSchema}
      onSubmit={onSubmit}
      defaultValues={{}}
    >
      {() => {
        return (
          <div className="flex flex-col gap-2">
            <p>
              This will remove the learning path and starting lesson for students. If there are any classes created or
              sessions generated, they will have to be re-created.
            </p>
          </div>
        );
      }}
    </FormModal>
  );
};
